import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import WixSDK from 'shared/utils/wix-sdk';
import {
  focusSource,
  focusWithin,
} from '@wix/wix-vod-shared/dist/src/common/accessibility/focus';
import _ from 'lodash';

import { getChannelForWidget } from '@wix/wix-vod-shared/dist/src/common/selectors/channels';
import {
  getAllSettings,
  getEditMode,
  getChannelLayout,
  getVideoIdFromAppSettings,
  isSingleVideo,
  isSliderLayout,
  isGridLayout,
} from 'shared/selectors/app-settings';

import ForceSliderHeight from 'widget/layouts/slider/containers/force-slider-height/force-slider-height';

import { canPlayFullOrPreview } from '@wix/wix-vod-shared/dist/src/widget/ui-selectors/video-access';
import {
  isCurrentComponentIdInUrl,
  getVideoIdFromUrl,
} from 'widget/selectors/query-params';
import { getMainVideoId } from '@wix/wix-vod-shared/dist/src/widget/ui-selectors/selected-video-id';
import { selectVideo } from 'widget/redux/client/actions/select-video';
import { getVideosGroupedByIds } from 'shared/selectors/videos';
import { getCurrentSiteUser } from 'widget/selectors/current-site-user';
import {
  openFullScreenVideoOverlay,
  closeFullScreenVideoOverlay,
} from 'widget/redux/client/actions/full-screen-modal';

import { getPublic as getChannel } from 'widget/redux/server/actions/channel/get-public';
import fetchCurrentSiteUser from 'widget/redux/server/actions/current-site-user/get';
import getPaymentInfo from 'widget/redux/server/actions/channel/payment';
import { getPublicVideo } from 'widget/redux/server/actions/channel/video/get';
import getExperiments from 'shared/redux/server/actions/experiments/get';
import {
  loadMoreVideoPages,
  prependVideoIds,
} from 'widget/redux/client/lazy-channel-videos/actions';

import TooltipPopout from 'shared/components/tooltip/tooltip-popout';
import Modal from 'widget/components/modal/modal';

import BiHandler from 'shared/bi/bi-handler';
import { PersistentEventsHandler } from 'widget/containers/persistent-events-handler/persistent-events-handler';
import { LayoutSpinner } from 'widget/layouts/components/spinner/spinner';
import { Loadable } from '@wix/wix-vod-shared/dist/src/common/components/loadable';

import LocaleProvider from 'shared/containers/locale-provider';

import WindowSize from 'shared/containers/window-size';
import { EditorObserver } from './components/editor-observer/editor-observer';
import { ViewerObserver } from 'shared/components/viewer-observer/viewer-observer';
import ReloadChannelPaymentDataOnPayment from 'widget/data-components/reload-channel-payment-data-on-payment';
import ClearVideoUrlsCacheOnPayment from 'widget/data-components/clear-video-urls-cache-on-payment';
import PaymentEventProxy from 'shared/components/payment-events/proxy';
import PaymentsBiHandler from 'widget/components/payments/bi-handler';
import PremiumIntentUpdater from './components/premium-intent-updater/premium-intent-updater';
import { hydratedData } from 'services/hydrated-data';

import styles from 'shared/styles/core.styl';

@connect(
  state => ({
    appSettings: getAllSettings(state),
    selectedVideoId: state.selectedVideoId,
    channelData: getChannelForWidget(state),
    currentSiteUser: getCurrentSiteUser(state),
    currentChannelId: state.currentChannelId,
    mainVideoId: getMainVideoId(state),
    videoIdFromUrl: getVideoIdFromUrl(state),
    editMode: getEditMode(state),
    layout: getChannelLayout(state),
    videos: getVideosGroupedByIds(state),
    isCompIdInUrl: isCurrentComponentIdInUrl(state),
    isSingleVideo: isSingleVideo(state),
    singleVideoId: getVideoIdFromAppSettings(state),
    windowSize: state.windowSize,
    isSliderLayout: isSliderLayout(state),
    isGridLayout: isGridLayout(state),
  }),
  {
    selectVideo,
    getExperiments,
    getChannel,
    fetchCurrentSiteUser,
    getPaymentInfo,
    getPublicVideo,
    loadMoreVideoPages,
    openFullScreenVideoOverlay,
    closeFullScreenVideoOverlay,
    prependVideoIds,
  },
)
export class DesktopView extends React.Component {
  static propTypes = {
    windowSize: PropTypes.object.isRequired,
    selectedVideoId: PropTypes.string,
    currentSiteUser: PropTypes.object,
    children: PropTypes.node,
    currentChannelId: PropTypes.string,
    mainVideoId: PropTypes.string,
    videoIdFromUrl: PropTypes.string,
    channelData: PropTypes.object,
    appSettings: PropTypes.object,
    selectVideo: PropTypes.func,
    getExperiments: PropTypes.func,
    getChannel: PropTypes.func,
    fetchCurrentSiteUser: PropTypes.func,
    getPaymentInfo: PropTypes.func,
    getPublicVideo: PropTypes.func,
    openFullScreenVideoOverlay: PropTypes.func,
    closeFullScreenVideoOverlay: PropTypes.func,
    editMode: PropTypes.string,
    layout: PropTypes.number,
    videos: PropTypes.object,
    isCompIdInUrl: PropTypes.bool,
    isSingleVideo: PropTypes.bool,
    singleVideoId: PropTypes.string,
    loadMoreVideoPages: PropTypes.func.isRequired,
    prependVideoIds: PropTypes.func.isRequired,
    isSliderLayout: PropTypes.bool.isRequired,
    isGridLayout: PropTypes.bool.isRequired,
  };

  componentDidMount() {
    // TODO: remove experiments fetching after opening video embed to all users
    this.props.getExperiments();
    this.focusWithinHandler = focusWithin();
    this.focusSourceHandler = focusSource();

    this.scrollToIfNecessary();
  }

  componentDidUpdate(prevProps) {
    const { isSingleVideo } = this.props;

    if (isSingleVideo) {
      this.updateWidgetSingleVideo(prevProps);
    }
  }

  componentWillUnmount() {
    this.focusWithinHandler.disengage();
    this.focusSourceHandler.disengage();
  }

  isPlayAllowed(id) {
    const { channelData, videos, currentSiteUser } = this.props;
    return canPlayFullOrPreview(channelData, videos[id], currentSiteUser);
  }

  handleDataLoaded = () => {
    const {
      videoIdFromUrl,
      videos,
      isSingleVideo,
      singleVideoId,
      channelData,
      isSliderLayout,
      openFullScreenVideoOverlay,
      closeFullScreenVideoOverlay,
      prependVideoIds,
      isGridLayout,
    } = this.props;

    const videoIdToSelect = isSingleVideo ? singleVideoId : videoIdFromUrl;

    if (channelData.featuredItem) {
      prependVideoIds(channelData.featuredItem);
    }

    if (!_.isEmpty(videos[videoIdToSelect])) {
      // TODO: this might be useless already, video should be loaded 100%

      if (isSliderLayout || isGridLayout) {
        const isAutoPlayable = this.isPlayAllowed(videoIdToSelect);
        openFullScreenVideoOverlay(
          channelData.id,
          videoIdToSelect,
          isAutoPlayable,
          closeFullScreenVideoOverlay,
        );
        return;
      }

      this.props.selectVideo(videoIdToSelect);
    }
  };

  scrollToIfNecessary() {
    const { videoIdFromUrl, isCompIdInUrl } = this.props;

    if (isCompIdInUrl || videoIdFromUrl) {
      WixSDK.navigateToCurrentComponent();
    }
  }

  updateWidgetSingleVideo(prevProps) {
    const {
      singleVideoId,
      selectVideo,
      selectedVideoId,
      videos,
      getPublicVideo,
      currentChannelId,
    } = this.props;

    if (prevProps.singleVideoId && prevProps.singleVideoId !== singleVideoId) {
      getPublicVideo(currentChannelId, singleVideoId);
      return;
    }

    if (
      !_.isEmpty(videos[singleVideoId]) &&
      selectedVideoId !== singleVideoId
    ) {
      selectVideo(singleVideoId);
    }
  }

  loadInitialData = () => {
    const {
      currentChannelId,
      isSingleVideo,
      singleVideoId,
      videoIdFromUrl,
    } = this.props;

    const {
      fetchCurrentSiteUser,
      getPaymentInfo,
      getChannel,
      getPublicVideo,
      loadMoreVideoPages,
    } = this.props;

    const videoId = isSingleVideo ? singleVideoId : videoIdFromUrl;
    const currentSiteUserPromise = fetchCurrentSiteUser();
    const paymentInfoPromise = getPaymentInfo(currentChannelId);
    const videoPromise = videoId
      ? getPublicVideo(currentChannelId, videoId)
      : Promise.resolve();

    return Promise.all([
      getChannel(currentChannelId),
      loadMoreVideoPages(),
      currentSiteUserPromise,
      paymentInfoPromise,
      videoPromise,
    ]).then(hydratedData.cleanup);
  };

  render() {
    const { children, appSettings, editMode, layout, windowSize } = this.props;

    return (
      <div
        data-layout={layout}
        data-app-version={window.__CI_APP_VERSION__}
        className={styles.root}
        data-view-mode={editMode}
      >
        <WindowSize />
        <BiHandler />
        <PaymentsBiHandler />
        {windowSize.width ? (
          <Loadable
            loader={this.loadInitialData}
            loading={LayoutSpinner}
            onDataLoaded={this.handleDataLoaded}
          >
            <div>
              <LocaleProvider>
                <ForceSliderHeight>{children}</ForceSliderHeight>
              </LocaleProvider>
              <PersistentEventsHandler />
            </div>
          </Loadable>
        ) : (
          <LayoutSpinner />
        )}
        <EditorObserver />
        <ViewerObserver />
        <PaymentEventProxy />
        <ReloadChannelPaymentDataOnPayment />
        <ClearVideoUrlsCacheOnPayment />

        <Modal settings={appSettings} />
        <TooltipPopout />
        <PremiumIntentUpdater />
      </div>
    );
  }
}
